import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import type { QuizQuestion } from '@shared/types';
import QuestionRenderer from '../quiz/QuestionRenderer';
import { Sheet } from './Sheet';
import { Pill } from './Pill';

/** Drop a trailing parenthetical hint like "(select all that apply)". */
const stripHint = (s: string) => s.replace(/\s*\([^)]*\)\s*$/, '').trim();

const isEmpty = (v: any) =>
  v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

function scaleLabel(question: QuizQuestion, value: number): string {
  const labels = question.scaleLabels;
  const max = question.scaleMax ?? 7;
  if (value <= 2) return labels?.min ?? String(value);
  if (value >= max - 1) return labels?.max ?? String(value);
  return 'Moderate';
}

/**
 * Collapsed field showing the selected answer(s); tapping opens the matching
 * quiz input (single / multiple / scale …) in a slide-up sheet. Reuses the
 * quiz's QuestionRenderer so the selectors look identical to the quiz.
 */
export function QuizFieldSelect({
  question,
  value,
  onChange,
  placeholder = 'Select',
}: {
  question: QuizQuestion;
  value: any;
  onChange: (value: any) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);

  // Resolve option-based answers (single/multiple) to displayable chips.
  const selectedValues = Array.isArray(value) ? value : isEmpty(value) ? [] : [value];
  const selectedOptions = question.options
    ? selectedValues
        .map((v) => question.options!.find((o) => o.value === v))
        .filter((o): o is NonNullable<typeof o> => !!o)
    : [];

  // Single-choice and scale resolve a single value, so close on selection.
  const closeOnPick = question.type === 'single' || question.type === 'scale';

  return (
    <div className="space-y-1.5">
      <label className="input-label">{stripHint(question.question)}</label>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="input-glass-wrapper w-full flex items-center justify-between gap-3 text-left hover:border-primary-400 dark:hover:border-primary-500 transition-colors"
      >
        {isEmpty(value) ? (
          <span className="text-neutral-400 dark:text-neutral-500 truncate">{placeholder}</span>
        ) : question.type === 'multiple' ? (
          <span className="flex flex-wrap gap-1.5 min-w-0">
            {selectedOptions.map((o) => (
              <Pill
                key={o.value}
                variant="soft"
                size="sm"
                leading={o.emoji ? <span>{o.emoji}</span> : undefined}
              >
                {o.label}
              </Pill>
            ))}
          </span>
        ) : question.options ? (
          <span className="flex items-center gap-2 min-w-0 text-neutral-900 dark:text-neutral-50">
            {selectedOptions[0]?.emoji && (
              <span className="leading-none">{selectedOptions[0].emoji}</span>
            )}
            <span className="truncate">{selectedOptions[0]?.label ?? String(value)}</span>
          </span>
        ) : (
          <span className="truncate text-neutral-900 dark:text-neutral-50">
            {question.type === 'scale' ? scaleLabel(question, value) : String(value)}
          </span>
        )}
        <ChevronRight className="w-4 h-4 text-neutral-400 flex-shrink-0" />
      </button>

      <Sheet open={open} onClose={() => setOpen(false)} title={stripHint(question.question)}>
        <QuestionRenderer
          question={question}
          value={value}
          onChange={onChange}
          onFinished={closeOnPick ? () => setOpen(false) : undefined}
        />
      </Sheet>
    </div>
  );
}
